import { sqlDataChat } from '@/services';
import type { ChatFlowStrategy, FlowContext } from './types';
import { useChatStore } from '@/store';
import { t } from '@/i18n';

/**
 * sqlDataChat 返回格式：
 *   fields: string[]      — 列名列表（表头）
 *   data:   string[][]    — 行数据，每行按 fields 下标对应列值
 *
 * 将其转换为 AssistantCard TableView 期望的 Record<string, string>[] 格式。
 */
const normalizeSqlData = (fields: string[], rows: string[][]): Record<string, string>[] =>
  rows.map((row) => Object.fromEntries(fields.map((field, i) => [field, row[i] ?? ''])));

/**
 * 分段模式流程（数据分析师角色专属）
 *
 * 第一阶段：sqlDataChat → 数据转换 → 渲染表格 + 显示「生成图表」按钮
 * 第二阶段（用户点击「生成图表」后，由 ChatPanel.handleGenerateChart 执行）：
 *   recordChat → 更新消息为图表 → recommendQuestions
 */
export const segmentFlow: ChatFlowStrategy = {
  async sendMessage(question: string, context: FlowContext) {
    const { currentRole } = useChatStore.getState();
    const {
      accessToken = '',
      chatId = '',
      addMessage,
      updateMessage,
      setLoading,
      setQuickQuestions,
      signal,
    } = context;

    addMessage({
      role: 'user',
      bizPayload: {
        role: currentRole.id,
        accessToken,
        chatId
      },
      content: { text: question },
      status: 'success'
    });

    const aiMsgId = addMessage({
      role: 'assistant',
      type: 'segment',
      bizPayload: {
        role: currentRole.id,
        accessToken,
        chatId,
      },
      content: { progressText: `- ${t('chart.queryingData')}`, progressStatus: 'running' },
      status: 'running',
    });

    setLoading(true);
    setQuickQuestions([]);

    try {
      const result = await sqlDataChat(chatId, question, accessToken, signal);
      if (result?.success === false) {
        updateMessage(aiMsgId, {
          content: { text: result.message || t('chat.answerFailed'), progressStatus: 'failed' },
          status: 'error',
        });
        return;
      }
      const recordId = String(result.chat_record_id ?? '');
      updateMessage(aiMsgId, { bizPayload: { chatRecordId: result.chat_record_id } })

      const { fields, data: rows } = result.data;
      const tableData = normalizeSqlData(fields, rows);

      updateMessage(aiMsgId, {
        type: 'segment',
        content: {
          text: result.title ?? '',
          progressStatus: 'succeeded',
          // type:'table' 触发 AssistantCard 的图表卡片分支，直接渲染表格视图
          chart: { type: 'table', title: result.title ?? '' },
          tableData,
          sql: result.sql,
          // 将 record_id 和 chatId 一起绑定在消息上：
          // 无论后续切换何种模式，点击「生成图表」时始终使用此消息自身的上下文
          pendingSqlRecordId: recordId,
          pendingSqlChatId: chatId,
        },
        status: 'success',
        // sqlDataChat 的 chat_record_id 等同于 record_id，用于反馈接口
        recordId: result.chat_record_id,
        awaitingChart: true,
      });
    } catch (error) {
      if (signal?.aborted) {
        updateMessage(aiMsgId, { content: { progressStatus: 'cancelled' }, status: 'cancelled' });
        return;
      }

      updateMessage(aiMsgId, {
        content: { text: t('chat.answerFailed'), progressStatus: 'failed' },
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  },
};
